// client/src/utils/pdfTemplates.js
import api from './api';
import { saveBlob } from './download';

function resolveDownloadFilename(headers, fallbackName = 'template.pdf') {
    const disposition = headers?.['content-disposition'] || headers?.['Content-Disposition'] || '';
    const utfMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utfMatch?.[1]) {
        return decodeURIComponent(utfMatch[1]).trim();
    }
    const plainMatch = disposition.match(/filename="?([^";]+)"?/i);
    if (plainMatch?.[1]) {
        return plainMatch[1].trim();
    }
    return fallbackName;
}

export async function fetchPdfTemplates() {
    const { data } = await api.get('/pdf-templates');
    return data;
}

export async function fetchAvailablePdfTemplates() {
    const { data } = await api.get('/pdf-templates/available');
    return data;
}

export async function fetchPdfTemplate(id) {
    const { data } = await api.get(`/pdf-templates/${id}`);
    return data;
}

export async function createPdfTemplate({
    file,
    name,
    category,
    jobTypeIds,
    isRepeatableInJobs,
}) {
    const formData = new FormData();
    formData.append('pdf', file);
    formData.append('name', name);
    if (category) {
        formData.append('category', category);
    }
    if (Array.isArray(jobTypeIds) && jobTypeIds.length > 0) {
        formData.append('jobTypeIds', JSON.stringify(jobTypeIds));
    }
    if (typeof isRepeatableInJobs === 'boolean') {
        formData.append('isRepeatableInJobs', String(isRepeatableInJobs));
    }
    const { data } = await api.post('/pdf-templates', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
}

export async function updatePdfTemplate(id, { name, category, file, isShareableByEmail, isRepeatableInJobs }) {
    if (file) {
        const formData = new FormData();
        if (name) formData.append('name', name);
        if (category) formData.append('category', category);
        if (typeof isShareableByEmail === 'boolean') {
            formData.append('isShareableByEmail', String(isShareableByEmail));
        }
        if (typeof isRepeatableInJobs === 'boolean') {
            formData.append('isRepeatableInJobs', String(isRepeatableInJobs));
        }
        formData.append('pdf', file);
        const { data } = await api.put(`/pdf-templates/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return data;
    }
    const { data } = await api.put(`/pdf-templates/${id}`, {
        name,
        category,
        isShareableByEmail,
        isRepeatableInJobs,
    });
    return data;
}

export async function savePdfTemplateSchema(id, schema) {
    const { data } = await api.put(`/pdf-templates/${id}/schema`, schema);
    return data;
}

export async function bulkUpdatePdfTemplateFields(payload) {
    const { data } = await api.patch('/pdf-templates/fields/bulk', payload);
    return data;
}

export async function checkPdfTemplateFieldBulkMatch(payload) {
    const { data } = await api.patch('/pdf-templates/fields/bulk', {
        ...payload,
        dryRun: true,
    });
    return data;
}

export async function downloadPdfTemplateFile(id, fallbackName = 'template.pdf') {
    const response = await api.get(`/pdf-templates/${id}/file`, {
        responseType: 'blob',
    });
    const filename = resolveDownloadFilename(response.headers, fallbackName);
    saveBlob(response.data, filename);
    return filename;
}

export async function deletePdfTemplate(id) {
    await api.delete(`/pdf-templates/${id}`);
}

export async function createTemplatePrefill(templateId, { name, valuesJson }) {
    const { data } = await api.post(`/pdf-templates/${templateId}/prefills`, { name, valuesJson });
    return data;
}

export async function updateTemplatePrefill(templateId, prefillId, { name }) {
    const { data } = await api.put(`/pdf-templates/${templateId}/prefills/${prefillId}`, { name });
    return data;
}

export async function deleteTemplatePrefill(templateId, prefillId) {
    await api.delete(`/pdf-templates/${templateId}/prefills/${prefillId}`);
}

export async function copyTemplatePrefill(templateId, payload) {
    const { data } = await api.post(`/pdf-templates/${templateId}/prefills/copy`, payload);
    return data;
}
